import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { RECEIVE_WARNING_SEVERITY, type ReceiveWarningDto } from '@/entities/receive';

interface ConfirmArrivalWarningsProps {
  warnings?: ReceiveWarningDto[];
}

/**
 * Warnings never block a receipt — they are informational, so they render as
 * alerts above the form rather than as validation errors. `severity` carries the
 * dangerous-goods rating: `X` (must be segregated) is the serious one, `O` (away
 * from) the milder.
 */
export function ConfirmArrivalWarnings({ warnings }: ConfirmArrivalWarningsProps) {
  const { t } = useTranslation();

  if (!warnings?.length) return null;

  return (
    <div className="space-y-2">
      {warnings.map((warning, index) => (
        <Alert
          key={`${warning.code}-${warning.locationId}-${index}`}
          variant={warning.severity === RECEIVE_WARNING_SEVERITY.SEGREGATED ? 'destructive' : 'warning'}>
          <AlertTriangle />
          <AlertDescription>
            {/* The backend localizes `message` from `messageKey`; the key is the
                fallback for a bundle entry the BE could not resolve. */}
            <span>{warning.message || t(warning.messageKey, warning.code)}</span>
            {warning.details?.length ? (
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs">
                {warning.details.map((detail, detailIndex) => (
                  <li key={`${detail.incomingLineId}-${detail.existingLineId}-${detailIndex}`}>
                    {t(
                      'receive.confirm.warning.dgPair',
                      'UN {{incomingUnId}} (class {{incomingClass}}) vs UN {{existingUnId}} (class {{existingClass}})',
                      {
                        incomingUnId: detail.incomingUnId,
                        incomingClass: detail.incomingClass,
                        existingUnId: detail.existingUnId,
                        existingClass: detail.existingClass
                      }
                    )}
                  </li>
                ))}
              </ul>
            ) : null}
          </AlertDescription>
        </Alert>
      ))}
    </div>
  );
}
